import {
  LoanApplicationCaseStatus,
  LoanApplicationCaseStatusLabelsForCreditLine,
  LoanApplicationStatus,
  LoanApplicationStatusLabels,
} from "../../../hooks/api/types";
import { isCreditFacility } from "../../../utils/loan-product.utils";
import { LOSPlatform } from "../../../contexts";
import { DateTime } from "luxon";
import {
  BulkParseStatus,
  DRAWDOWN_ELIGIBLE_STATUSES,
  InvoiceEntryMode,
  POST_ACTIVE_STATUSES,
} from "./constants";

type LoanForDateDisplay = {
  externalId?: number;
  loanType?: string;
  creditLine?: { endDate?: string };
  losPlatform?: string;
  caseStatus?: LoanApplicationCaseStatus | null;
  status?: LoanApplicationStatus;
  submittedAt?: string | null;
  loanClosureDate?: string | null;
};

export type BulkParseSuccessRow = {
  invoiceNo?: string;
  invoiceAmount?: number;
  requestedAmount?: number;
  issuedDate?: string;
  dueDate?: string;
};

export type BulkParseErrorRow = {
  row?: number;
  message?: string;
};

export type BulkParseExtracted = {
  totalAmount?: number;
  numberOfInvoices?: number;
  errors?: BulkParseErrorRow[];
  successes?: BulkParseSuccessRow[];
};

/** UI state for bulk template parse flow (parent owns state; form receives as prop). */
export type BulkParseState = BulkParseExtracted & {
  status: BulkParseStatus;
};

export type InvoiceEntryModeType =
  (typeof InvoiceEntryMode)[keyof typeof InvoiceEntryMode];

export const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : undefined;

export const asArray = <T = unknown>(value: unknown): T[] =>
  Array.isArray(value) ? (value as T[]) : [];

export const toNumberOrUndefined = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
};

export const toStringOrUndefined = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() !== "" ? value : undefined;

export const toRowNumberOrUndefined = (value: unknown): number | undefined => {
  const n = toNumberOrUndefined(value);
  return typeof n === "number" ? n : undefined;
};

const formatDate = (date: string) =>
  DateTime.fromISO(date).toFormat("dd LLL yyyy");

/**
 * Shared date display for facility cards: "Valid until", "Closed on", "Due date", or "Applied on".
 */
export const getLoanDateDisplay = (loan: LoanForDateDisplay): string | null => {
  const isCreditLine = isCreditFacility(loan.loanType);

  const displayStatus =
    loan.losPlatform === LOSPlatform.CREDIBLEX
      ? loan.caseStatus != null
        ? LoanApplicationCaseStatusLabelsForCreditLine[loan.caseStatus!]?.label
        : undefined
      : loan.status != null
      ? LoanApplicationStatusLabels[loan.status!]?.label
      : undefined;

  const contractClosureDate = loan.loanClosureDate ?? undefined;
  const endDate = loan.creditLine?.endDate;

  if (displayStatus === "Closed") {
    const closedDate = contractClosureDate || endDate;
    return closedDate ? `Closed on ${formatDate(closedDate)}` : "Closed";
  }
  if (displayStatus === "Active") {
    const validUntilDate = endDate || contractClosureDate;

    return validUntilDate
      ? `Valid until ${formatDate(validUntilDate)}`
      : "valid until -";
  }

  if (isCreditLine && endDate) {
    if (displayStatus === "Overdue" || displayStatus === "Late Fee Applied") {
      return `Due date ${formatDate(endDate)}`;
    }
  }

  return loan.submittedAt ? `Applied on ${formatDate(loan.submittedAt)}` : null;
};

export const shouldIssueDrawdown = (status: LoanApplicationCaseStatus) => {
  return DRAWDOWN_ELIGIBLE_STATUSES.includes(status);
};

export const isPostActiveStatus = (status: LoanApplicationCaseStatus) => {
  return POST_ACTIVE_STATUSES.includes(status);
};

export const toDateOrUndefined = (value: unknown): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (typeof value === "string" || typeof value === "number") {
    const d = new Date(value);
    return isNaN(d.getTime()) ? undefined : d;
  }
  return undefined;
};
